import isEmpty from 'lodash/isEmpty';

import type { CabinClass, TripType } from '@eti/schema-types';
import { EventSegmentType, FilterType } from '@eti/schema-types';
import {
  CHILD_AGE_DEFAULT_VALUE,
  MAX_PASSENGERS,
  MAX_INFANT_AGE,
} from '../../../widgets/search-form/constants/passengers';
import { cabinClasses } from '../../../constants/cabinClassesConstants';
import { getAgesOfChildren, getAgesOfInfants } from '../../../utils/format';
import type {
  SearchContextTravelers,
  SearchData,
  SearchType,
  RoutesType,
  SearchOnResultPageOriginType,
  NonNullableRoutesType,
} from '../models';
import { getReturnDate, getReturnTimeOfDay } from './searchUtils';
import { convertDateStringOrTimestamp } from './dateUtils';

const transformRegion = (region: SearchOnResultPageOriginType | null) => {
  if (region === null) {
    return null;
  }

  return {
    iataCode: region.code,
    name: region.name,
    city: region.cityName,
    cityCode: region.cityCode,
    continent: region.continentName,
    continentCode: region.continentCode,
    country: region.countryName,
    countryCode: region.countryCode,
  };
};

const transformRoute = (route: RoutesType[number]) => {
  return {
    origin: transformRegion(route?.origin ?? null),
    destination: transformRegion(route?.destination ?? null),
    departureDate: route?.departureAt
      ? new Date(convertDateStringOrTimestamp(route.departureAt))
      : null,
    departureTimeOfDay: route?.departureTimeOfDay,
  };
};
export const getChildrenAges = (noOfChildren: number, childAgeArray: number[]) =>
  childAgeArray.slice(0, noOfChildren);

export const transformPassengersOfEditPassengers = (searchData: {
  adults: number;
  children: number;
  infants: number;
  childrenAges: number[];
  infantsAges: number[];
}) => {
  const arrayOfChildAges = getChildrenAges(searchData.children, searchData.childrenAges);

  const arrayOfInfantAges = getChildrenAges(searchData.infants, searchData.infantsAges);

  return {
    adults: searchData.adults,
    children: arrayOfChildAges.length,
    infants: arrayOfInfantAges.length,
    childrenAges: arrayOfChildAges,
    infantsAges: arrayOfInfantAges,
  };
};

export const transformTravelersOfSearchContext = (
  searchData: SearchContextTravelers,
): {
  adults: number;
  children: number;
  infants: number;
  childrenAges: number[];
  infantsAges: number[];
} => {
  const arrayOfChildAges = getAgesOfChildren(searchData.childAges);
  const arrayOfInfantAges = getAgesOfInfants(searchData.childAges);
  const updatedChildrenAge = [
    ...arrayOfChildAges,
    ...Array(MAX_PASSENGERS - (arrayOfChildAges.length || 0)).fill(CHILD_AGE_DEFAULT_VALUE),
  ];

  const updatedInfantAge = [
    ...arrayOfInfantAges,
    ...Array(MAX_PASSENGERS - (arrayOfInfantAges.length || 0)).fill(MAX_INFANT_AGE),
  ];
  return {
    adults: searchData.adults,
    children: arrayOfChildAges.length,
    infants: arrayOfInfantAges.length,
    childrenAges: updatedChildrenAge,
    infantsAges: updatedInfantAge,
  };
};

export type TransformSearchResultDataReturnType =
  | (Omit<NonNullable<SearchType>, 'travelers'> & {
      cabinClass: CabinClass;
      destination: {
        iataCode?: string;
        name?: string;
        city?: string;
        cityCode?: string;
        continent?: string;
        continentCode?: string;
        country?: string;
        countryCode?: string;
        departuredAt: Date | null;
      };
      directFlight: boolean;
      origin: {
        iataCode?: string;
        name?: string;
        city?: string;
        cityCode?: string;
        continent?: string;
        continentCode?: string;
        country?: string;
        countryCode?: string;
        departuredAt: Date | null;
      };
      passengers: {
        adults: number;
        children: number;
        infants: number;
        childrenAges: number[];
        infantsAges: number[];
      } | null;
      stopoverEnabled: boolean;
      shortStopover: boolean;
      oneDayStopover: boolean;
      oneNightStopover: boolean;
      twoNightsStopover: boolean;
      singleBound: {
        origin: null | {
          iataCode: string;
          name: string;
          city: string;
          cityCode: string;
          continent: string;
          continentCode: string;
          country: string;
          countryCode: string;
        };
        destination: null | {
          iataCode: string;
          name: string;
          city: string;
          cityCode: string;
          continent: string;
          continentCode: string;
          country: string;
          countryCode: string;
        };
        departureDate: Date | null;
        departureTimeOfDay?: 'AFTERNOON' | 'ANY' | 'MORNING' | 'NOON' | null;
        returnDate: Date | null;
        returnTimeOfDay?: 'AFTERNOON' | 'ANY' | 'MORNING' | 'NOON' | null;
      };
      multiBound: {
        origin: null | {
          iataCode: string;
          name: string;
          city: string;
          cityCode: string;
          continent: string;
          continentCode: string;
          country: string;
          countryCode: string;
        };
        destination: null | {
          iataCode: string;
          name: string;
          city: string;
          cityCode: string;
          continent: string;
          continentCode: string;
          country: string;
          countryCode: string;
        };
        departureDate: Date | null;
        departureTimeOfDay?: 'AFTERNOON' | 'ANY' | 'MORNING' | 'NOON' | null;
      }[];
      tripType: TripType;
    })
  | null;

export const transformSearchResultData = (
  currentSearchData: SearchData,
  resultData: SearchType,
): TransformSearchResultDataReturnType => {
  if (isEmpty(resultData)) {
    return null;
  }

  const { type: tripType } = resultData;
  const searchResultsRoutes: NonNullableRoutesType[] = resultData.routes.filter(
    (route) => route !== null,
  );
  const [firstRoute] = searchResultsRoutes;

  const singleBound = {
    ...transformRoute(firstRoute),
    returnDate: getReturnDate(tripType, searchResultsRoutes),
    returnTimeOfDay: getReturnTimeOfDay(tripType, searchResultsRoutes),
  };

  const multiBound = searchResultsRoutes.map((route) => transformRoute(route));

  const passengers = currentSearchData?.travelers
    ? transformTravelersOfSearchContext(currentSearchData?.travelers)
    : null;

  const cabinClass = currentSearchData?.cabinClass
    ? cabinClasses[currentSearchData.cabinClass]
    : cabinClasses.ECONOMY;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { travelers, ...rest } = resultData;

  const typedRest: Omit<NonNullable<SearchType>, 'travelers'> = rest;

  return {
    ...typedRest,
    routes: searchResultsRoutes,
    cabinClass,
    carrierCodes: currentSearchData.carrierCodes ?? [],
    destination: {
      ...transformRegion(firstRoute?.destination ?? null),
      departuredAt: getReturnDate(tripType, searchResultsRoutes),
    },
    directFlight: currentSearchData?.filters?.[FilterType.MaxStops] === '0',
    origin: {
      ...transformRegion(firstRoute?.origin ?? null),
      departuredAt: new Date(firstRoute?.departureAt ?? ''),
    },
    passengers,
    stopoverEnabled: Boolean(currentSearchData?.filters?.[FilterType.StopoverTypes]),
    shortStopover: Boolean(
      currentSearchData?.filters?.[FilterType.StopoverTypes]?.includes(
        EventSegmentType.ShortStopover,
      ),
    ),
    oneDayStopover: Boolean(
      currentSearchData?.filters?.[FilterType.StopoverTypes]?.includes(
        EventSegmentType.OneDayStopover,
      ),
    ),
    oneNightStopover: Boolean(
      currentSearchData?.filters?.[FilterType.StopoverTypes]?.includes(
        EventSegmentType.OneNightStopover,
      ),
    ),
    twoNightsStopover: Boolean(
      currentSearchData?.filters?.[FilterType.StopoverTypes]?.includes(
        EventSegmentType.TwoNightsStopover,
      ),
    ),
    singleBound,
    multiBound,
    tripType,
  };
};
